import { forwardRef, memo, useCallback, useImperativeHandle, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, MediaSize, Point, Size } from 'react-easy-crop/types';
import { Slider as AntSlider } from 'antd';
import {
  FREE_ASPECT,
  INIT_ROTATE,
  INIT_ZOOM,
  MAX_ROTATE,
  MIN_CROP_SIZE,
  MIN_ROTATE,
  PREFIX,
  RESIZE_HANDLES,
  ROTATE_STEP,
  ZOOM_STEP,
} from './constants';
import type { EasyCropProps, EasyCropRef, ResizeHandle } from './types';

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

// eslint-disable-next-line react/display-name
const EasyCrop = forwardRef<EasyCropRef, EasyCropProps>((props, ref) => {
  const {
    cropperRef,
    image,

    aspect,
    shape,
    grid,
    zoom,
    rotate,
    minZoom,
    maxZoom,
    cropperProps,
  } = props;

  const isFreeCrop = aspect === FREE_ASPECT;

  const [crop, onCropChange] = useState<Point>({ x: 0, y: 0 });
  const [cropSize, setCropSize] = useState<Size>({ width: 0, height: 0 });
  const [zoomVal, setZoomVal] = useState(INIT_ZOOM);
  const [rotateVal, setRotateVal] = useState(INIT_ROTATE);
  const cropPixelsRef = useRef<Area>({ width: 0, height: 0, x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaSizeRef = useRef<Size>({ width: 0, height: 0 });
  // latest values, needed as the starting point of a resize drag
  const cropRef = useRef(crop);
  const cropSizeRef = useRef(cropSize);
  cropRef.current = crop;
  cropSizeRef.current = cropSize;

  const onMediaLoaded = useCallback(
    (mediaSize: MediaSize) => {
      const { width, height } = mediaSize;
      mediaSizeRef.current = { width, height };

      if (isFreeCrop) {
        // start with the whole image selected, the user resizes it from there
        setCropSize({ width, height });
        return;
      }

      const ratioWidth = height * (aspect as number);

      if (width > ratioWidth) {
        setCropSize({ width: ratioWidth, height });
      } else {
        setCropSize({ width, height: width / (aspect as number) });
      }
    },
    [aspect, isFreeCrop],
  );

  // the crop box is always centred in the container, so resizing it grows/shrinks
  // both sides — the media is shifted by half the delta to keep the opposite edge still
  const onResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, handle: ResizeHandle) => {
      event.preventDefault();
      event.stopPropagation();

      const target = event.currentTarget;
      const startX = event.clientX;
      const startY = event.clientY;
      const startCrop = cropRef.current;
      const startSize = cropSizeRef.current;
      const container = containerRef.current?.getBoundingClientRect();
      const media = mediaSizeRef.current;

      const dirX = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
      const dirY = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;
      const maxWidth = Math.min(container?.width ?? startSize.width, media.width * zoomVal);
      const maxHeight = Math.min(container?.height ?? startSize.height, media.height * zoomVal);

      const onPointerMove = (moveEvent: PointerEvent) => {
        const width = dirX
          ? clamp(startSize.width + dirX * (moveEvent.clientX - startX), MIN_CROP_SIZE, maxWidth)
          : startSize.width;
        const height = dirY
          ? clamp(startSize.height + dirY * (moveEvent.clientY - startY), MIN_CROP_SIZE, maxHeight)
          : startSize.height;

        setCropSize({ width, height });
        onCropChange({
          x: startCrop.x - (dirX * (width - startSize.width)) / 2,
          y: startCrop.y - (dirY * (height - startSize.height)) / 2,
        });
      };

      const onPointerUp = () => {
        target.removeEventListener('pointermove', onPointerMove);
        target.removeEventListener('pointerup', onPointerUp);
        target.removeEventListener('pointercancel', onPointerUp);
      };

      target.setPointerCapture(event.pointerId);
      target.addEventListener('pointermove', onPointerMove);
      target.addEventListener('pointerup', onPointerUp);
      target.addEventListener('pointercancel', onPointerUp);
    },
    [zoomVal],
  );

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    cropPixelsRef.current = croppedAreaPixels;
  }, []);

  useImperativeHandle(ref, () => ({
    rotateVal,
    setZoomVal,
    setRotateVal,
    cropPixelsRef,
  }));
  return (
    <>
      <div className={`${PREFIX}-wrapper`} ref={containerRef}>
        <Cropper
          {...cropperProps}
          ref={cropperRef}
          image={image}
          crop={crop}
          cropSize={cropSize}
          onCropChange={onCropChange}
          aspect={isFreeCrop ? cropSize.width / cropSize.height || 1 : (aspect as number)}
          cropShape={shape}
          showGrid={grid}
          zoomWithScroll={zoom}
          zoom={zoomVal}
          rotation={rotateVal}
          onZoomChange={setZoomVal}
          onRotationChange={setRotateVal}
          minZoom={minZoom}
          maxZoom={maxZoom}
          onMediaLoaded={onMediaLoaded}
          onCropComplete={onCropComplete}
          classes={{
            containerClassName: `${PREFIX}-container`,
            mediaClassName: `${PREFIX}-media`,
          }}
        />
        {isFreeCrop && cropSize.width > 0 && cropSize.height > 0 && (
          <div
            className={`${PREFIX}-resize`}
            style={{ width: cropSize.width, height: cropSize.height }}>
            {RESIZE_HANDLES.map((handle) => (
              <div
                key={handle}
                className={`${PREFIX}-resize-handle ${PREFIX}-resize-handle-${handle}`}
                onPointerDown={(event) => onResizeStart(event, handle)}
              />
            ))}
          </div>
        )}
      </div>
      {zoom && (
        <section className={`${PREFIX}-control ${PREFIX}-control-zoom`}>
          <button
            onClick={() => setZoomVal(zoomVal - ZOOM_STEP)}
            disabled={zoomVal - ZOOM_STEP < minZoom}>
            －
          </button>
          <AntSlider
            min={minZoom}
            max={maxZoom}
            step={ZOOM_STEP}
            value={zoomVal}
            onChange={setZoomVal}
          />
          <button
            onClick={() => setZoomVal(zoomVal + ZOOM_STEP)}
            disabled={zoomVal + ZOOM_STEP > maxZoom}>
            ＋
          </button>
        </section>
      )}
      {rotate && (
        <section className={`${PREFIX}-control ${PREFIX}-control-rotate`}>
          <button
            onClick={() => setRotateVal(rotateVal - ROTATE_STEP)}
            disabled={rotateVal === MIN_ROTATE}>
            ↺
          </button>
          <AntSlider
            min={MIN_ROTATE}
            max={MAX_ROTATE}
            step={ROTATE_STEP}
            value={rotateVal}
            onChange={setRotateVal}
          />
          <button
            onClick={() => setRotateVal(rotateVal + ROTATE_STEP)}
            disabled={rotateVal === MAX_ROTATE}>
            ↻
          </button>
        </section>
      )}
    </>
  );
});

export default memo(EasyCrop);
